import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Loader2, MessageSquareWarning } from "lucide-react";
import api from "../lib/api";
import { reportPrefill } from "../lib/guide";
import { reportHref } from "../lib/portalTickets";
import GuideBrowser from "../components/guide/GuideBrowser";
import { Shell } from "../components/portal/PortalShell";

/**
 * `/portal/ghid` — the guide in the portal (19.10.2026). The server gives each
 * person only the chapters of their roles, and of the regimes of their groups;
 * a trainer who also evaluates sees both. What is missing goes to the portal's
 * report form, already naming the article, as a ticket about the guide.
 */
export default function PortalGuide() {
  const [index, setIndex] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    api.get("/portal/guide")
      .then((r) => setIndex(r.data))
      .catch((err) => setError(err?.response?.status === 401 ? "out" : "Ghidul nu se poate deschide acum. Încearcă din nou."));
  }, []);

  const footer = (article) => {
    const p = reportPrefill(article);
    const page = article ? `/portal/ghid?a=${article.id}` : "/portal/ghid";
    return (
      <div className="mt-8 flex flex-wrap items-center gap-3 rounded-xl bg-[#F5F6F6] px-4 py-3 text-sm text-[#4B5160]">
        <span className="min-w-[12rem] flex-1">{article ? "Lipsește ceva din articol sau nu mai e așa?" : "Nu găsești ce cauți?"}</span>
        <Link to={reportHref(page, { kind: "suggestion", title: p.title, description: p.description })}
          className="inline-flex items-center gap-1.5 rounded-full border border-[#965EA7]/40 px-3 py-1.5 font-semibold text-[#7A4986] hover:bg-[#965EA7]/10"
          data-testid="portal-guide-report">
          <MessageSquareWarning size={14} aria-hidden="true" /> Spune-ne
        </Link>
      </div>
    );
  };

  const welcome = (
    <>
      <h1 className="font-display text-2xl font-bold text-[#373A45]">Ghidul portalului</h1>
      <p className="mt-2 text-[15px] leading-relaxed text-[#4B5160]">
        Cum folosești portalul, pas cu pas. Vezi doar articolele care te privesc. Caută un cuvânt sau alege un
        articol din listă.
      </p>
      {index && !index.chapters.length && (
        <p className="mt-4 rounded-xl bg-[#F5F6F6] px-4 py-3 text-sm text-[#4B5160]" data-testid="portal-guide-empty">
          Încă nu avem articole pentru tine. Le scriem acum. Până atunci, pentru orice întrebare, scrie-ne la
          contact@fped.org.
        </p>
      )}
    </>
  );

  return (
    <Shell widest report={false}>
      <Link to="/portal" className="inline-flex items-center gap-1.5 text-sm text-[#6B7280] hover:text-[#965EA7]">
        <ArrowLeft size={14} /> Înapoi la portal
      </Link>
      <div className="mt-6">
        {error === "out" && (
          <p className="text-sm text-[#373A45]">
            Sesiunea s-a încheiat. <Link to="/portal" className="font-semibold text-[#965EA7]">Intră din nou</Link>, apoi
            deschide ghidul.
          </p>
        )}
        {error && error !== "out" && <p className="text-sm text-[#c0483f]" role="alert">{error}</p>}
        {!error && !index && <div className="py-24 text-center text-[#6B7280]"><Loader2 className="mx-auto animate-spin" /></div>}
        {index && <GuideBrowser apiBase="/portal/guide" index={index} footer={footer} welcome={welcome} stickyTop="lg:top-6"
          placeholder="Caută: temă, prezență, portofoliu…" />}
      </div>
    </Shell>
  );
}
